/**
 * Reconcile a preferred model selection with what the user is actually allowed
 * to dispatch.
 *
 * The preference chain (`resolvePreferredComposerModelSelection`) answers "what
 * would this thread start with", and it ends in a hardcoded per-provider default
 * that knows nothing about policy. For an account with a full model allowlist
 * that is fine. For the non-technical profile — granted one agent and no models
 * at all — it produces a selection the server refuses, and the user meets
 * "Model 'claudeAgent/…' not allowed for user" on their first message with no
 * way to fix it from the picker.
 *
 * This module is the missing step: given the allowed models and the agents the
 * user can see, it swaps an unusable selection for a usable one. A managed agent
 * is a valid answer here because it carries its own provider and model, and the
 * server exempts an agent's own model from the model allowlist.
 */
import type { ManagedAgent, ManagedAgentId, ModelSelection } from "@taskin-code/contracts";
import { canRunAsPrimary } from "@taskin-code/shared/managedAgentMode";
import { buildAgentModelSelection } from "@taskin-code/shared/managedAgentModelOptions";
import { isModelSelectionAllowed } from "@taskin-code/shared/modelPolicy";

export interface AllowedModelSelectionInput {
  /** What the preference chain produced. */
  readonly preferred: ModelSelection;
  /**
   * `"provider:model"` keys the user may dispatch. An empty set means the
   * account was granted no models, which is a real configuration and not an
   * "everything goes" shortcut.
   */
  readonly allowedModelKeys: ReadonlySet<string>;
  /** Agents the user can see, already filtered by the server. */
  readonly agents: ReadonlyArray<ManagedAgent>;
  /** The user's configured default agent, if any. */
  readonly defaultAgentId: ManagedAgentId | null;
}

/**
 * The policy half of the input, without the selection being reconciled.
 *
 * Passed around as one value so a call site cannot thread two of the three
 * fields and silently reconcile against a half-built policy.
 */
export type ModelSelectionPolicy = Omit<AllowedModelSelectionInput, "preferred">;

/**
 * Through the shared builder so the agent's pinned effort travels with it. The
 * server imposes it anyway (`resolveCommandModelSelection`), but a selection
 * built here without it would disagree with the one the server resolves, and
 * `modelSelectionDiffers` would then dispatch a `thread.meta.update` before
 * every turn.
 */
const selectionForAgent = (agent: ManagedAgent): ModelSelection => buildAgentModelSelection(agent);

/**
 * Order matters and encodes intent:
 *
 * 1. An explicit agent already in the selection wins outright — the user (or a
 *    project default) picked it, and its model is exempt from the allowlist.
 * 2. An allowed model is left alone. Policy is satisfied; do not second-guess.
 * 3. Otherwise the selection is unusable, and the best replacement is the
 *    user's default agent, then their only agent — counted among the agents a
 *    person may actually run, not among all the visible ones: the list carries
 *    subagent-only agents so the transcript can name them, and an account with
 *    one runnable agent plus one delegation target must still resolve to the
 *    runnable one. "Only" is deliberate: with several agents and no default there
 *    is no non-arbitrary pick, and the picker shows them all for the user to
 *    choose.
 * 4. Failing that, any allowed model, so an account with models but a stale
 *    preference still starts somewhere legal.
 * 5. Failing everything, the preference is returned untouched — nothing here can
 *    invent a permission, and the server error is the honest outcome.
 */
export const resolveAllowedModelSelection = (input: AllowedModelSelectionInput): ModelSelection => {
  const { preferred, allowedModelKeys, agents, defaultAgentId } = input;

  // Rule 1 asks the *unfiltered* list on purpose: a delegated thread runs on a
  // subagent-only agent, and rewriting its selection out from under it would
  // restart the child on a different agent than the one that was told to do the
  // work.
  if (preferred.agentId && agents.some((agent) => agent.id === preferred.agentId)) {
    return preferred;
  }

  if (allowedModelKeys.has(`${preferred.provider}:${preferred.model}`)) {
    return preferred;
  }

  const runnableAgents = agents.filter((agent) => canRunAsPrimary(agent.mode));

  const defaultAgent = defaultAgentId
    ? (runnableAgents.find((agent) => agent.id === defaultAgentId) ?? null)
    : null;
  if (defaultAgent) return selectionForAgent(defaultAgent);

  const soleAgent = runnableAgents.length === 1 ? runnableAgents[0] : undefined;
  if (soleAgent) return selectionForAgent(soleAgent);

  const firstAllowed = [...allowedModelKeys][0];
  if (firstAllowed) {
    const separator = firstAllowed.indexOf(":");
    const provider = firstAllowed.slice(0, separator);
    const model = firstAllowed.slice(separator + 1);
    if (provider === "codex" || provider === "claudeAgent") {
      return { provider, model } as ModelSelection;
    }
  }

  return preferred;
};

/**
 * `resolveAllowedModelSelection` with an explicit "policy not known" case.
 *
 * A `null` policy returns the preference untouched, which is what every path
 * must do before `/auth/me` has answered and when auth is disabled altogether:
 * an unloaded allowlist is indistinguishable from an empty one, and reconciling
 * against it would hand an agent to a user who has models and never asked.
 */
export const applyModelSelectionPolicy = (
  preferred: ModelSelection,
  policy: ModelSelectionPolicy | null | undefined,
): ModelSelection => (policy ? resolveAllowedModelSelection({ preferred, ...policy }) : preferred);

/**
 * Whether the user may dispatch this selection, agent exemption included.
 *
 * Shares `isModelSelectionAllowed` with the server so the badge cannot claim a
 * selection is forbidden while the command gate lets it through — which is the
 * bug this replaced: an account granted an agent and no models saw "Modelo no
 * autorizado" on a selection the server was perfectly happy to run.
 */
export const isSelectionAllowedByPolicy = (input: {
  readonly selection: ModelSelection | null | undefined;
  readonly allowedModelKeys: ReadonlySet<string>;
  readonly agents: ReadonlyArray<ManagedAgent>;
}): boolean => {
  if (!input.selection) return true;
  const agent = input.selection.agentId
    ? (input.agents.find((entry) => entry.id === input.selection?.agentId) ?? null)
    : null;
  return isModelSelectionAllowed({
    selection: input.selection,
    allowedModelKeys: input.allowedModelKeys,
    agent,
  });
};
